import React from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { PROFILE_STRENGTH_QUERY } from '../graphql';

const PROMPT_ACTIONS = {
  'Add a bio': '/profile',
  'Add at least': '/profile',
  'Add your city': '/profile',
  'Add your age': '/profile',
  'Add your building': '/profile',
  'Join your first group': '/',
};

function resolveAction(msg) {
  for (const [key, path] of Object.entries(PROMPT_ACTIONS)) {
    if (msg.startsWith(key)) return path;
  }
  return '/profile';
}

function ScoreRing({ score }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  const color = score >= 80 ? '#10b981' : score >= 50 ? '#f97316' : '#ef4444';
  return (
    <svg width={72} height={72} viewBox="0 0 72 72" style={{ flexShrink: 0 }}>
      <circle cx={36} cy={36} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={7} />
      <circle
        cx={36} cy={36} r={r}
        fill="none"
        stroke={color}
        strokeWidth={7}
        strokeDasharray={`${filled} ${circ - filled}`}
        strokeDashoffset={circ / 4}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
      <text x={36} y={40} textAnchor="middle" fill={color} fontSize={14} fontWeight={700}>{score}%</text>
    </svg>
  );
}

export default function ProfileStrength({ compact = false }) {
  const navigate = useNavigate();
  const { data, loading } = useQuery(PROFILE_STRENGTH_QUERY);

  if (loading || !data?.profileStrength) return null;
  const { score, missing } = data.profileStrength;
  if (score === 100) return null;

  const label = score >= 80 ? 'Strong' : score >= 50 ? 'Good' : 'Getting started';

  if (compact) {
    return (
      <div
        onClick={() => navigate('/profile')}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
          padding: '8px 14px', background: 'var(--surface)', borderRadius: 12,
          border: '1px solid var(--border)', marginBottom: 16,
        }}
      >
        <ScoreRing score={score} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Profile {label} · {score}%</div>
          {missing[0] && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Next: {missing[0]}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 16, padding: '20px 24px', marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: missing.length ? 16 : 0 }}>
        <ScoreRing score={score} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Profile Strength: {label}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
            {score < 100 ? `Complete your profile to get better group & buddy matches` : 'Your profile is complete!'}
          </div>
        </div>
      </div>

      {missing.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {missing.map((msg, i) => (
            <button
              key={i}
              onClick={() => navigate(resolveAction(msg))}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
                borderRadius: 10, padding: '10px 14px', cursor: 'pointer', textAlign: 'left',
                color: 'var(--text)', width: '100%',
              }}
            >
              <span style={{ fontSize: 16 }}>➕</span>
              <span style={{ fontSize: 13, flex: 1 }}>{msg}</span>
              <span style={{ color: 'var(--primary)', fontSize: 13, fontWeight: 600 }}>Fix →</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
